import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { format, parseJSON } from "date-fns";
import { IconButton, Typography, useTheme } from "@mui/material";
import { BookingTypeEnum } from "@rentablo/api/apiv1";
import { GainLossTypography } from "@rentablo/components";
import CancelledIcon from "@icons/myicons/cancelled.svg?react";
import ChatIcon from "@icons/myicons/chat.svg?react";
import {
  getNumberOfLotsByInvestmentAndBookingType,
  getValueByInvestmentAndBookingType,
  toCurrency,
  toGermanNumberFormat,
  toPercent,
} from "@helpers";
import { BookingType } from "@components";
import DotsIcon from "@icons/myicons/dots.svg?react";
import { MobileListRow } from "@components/MobileList";
import {
  ResponsiveDropdown,
  ResponsiveDropdownItem,
} from "@components/ResponsiveDropdown";
import { TableBookingData } from "../BookingsSingleInvestmentTable";
import { BookingsTableDeleteAction } from "../BookingsTableDeleteAction";
import {
  TransactionStatusIcon,
  transactionNeedsAttention,
} from "../TransactionStatusIcon";

const CENTS = { minimumFractionDigits: 2 };
const LOTLESS_TYPES: BookingTypeEnum[] = ["dividend", "purchase_right"];

interface Props {
  booking: TableBookingData;
}

export const InvestmentBookingMobileRow = ({ booking }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  // "10 × € 94,02" as in the depot's bookings list; splits show their ratio,
  // dividends their amount per share alone.
  const isRatio =
    booking.bookingType === "split" || booking.bookingType === "reverse_split";
  const lots = isRatio
    ? getNumberOfLotsByInvestmentAndBookingType({
        value: booking.numberOfLots,
        investmentType: booking.investmentType,
        bookingType: booking.bookingType,
      })
    : booking.numberOfLots && !LOTLESS_TYPES.includes(booking.bookingType)
      ? toGermanNumberFormat(booking.numberOfLots, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 4,
        })
      : "";
  const price = booking.securityPrice
    ? getValueByInvestmentAndBookingType({
        value: booking.securityPrice,
        investmentType: booking.investmentType,
        bookingType: booking.bookingType,
        percentageRange: "0-100",
        currency: booking.currency,
        quoteNotation: booking.quoteNotation,
        currencyOptions: CENTS,
      })
    : "";
  const quantity = [lots, price].filter(Boolean).join(isRatio ? " " : " × ");
  const status = {
    dataConsistencyStatus: booking.dataConsistencyStatus,
    isExternalID: booking.isExternalID,
  };
  const iconStyle: React.CSSProperties = {
    flex: "none",
    color: theme.palette.icon.secondary,
  };

  const date = format(parseJSON(booking.valueDate), "dd.MM.yyyy");
  const canEdit =
    booking.bookingType !== "split" && booking.bookingType !== "reverse_split";
  const actions: ResponsiveDropdownItem[] = [
    ...(canEdit
      ? [
          {
            label: t("investment.protocolTable.actions.edit"),
            to:
              booking.bookingType === "dividend" ||
              booking.bookingType === "purchase_right"
                ? `/investments/${booking.investmentId}/dividend-wizard/edit/${booking.id}`
                : `/edit-transaction-wizard/${booking.id}`,
          },
        ]
      : []),
    {
      label: t("investment.protocolTable.actions.delete"),
      color: "error.main",
      onClick: () => setIsDeleteOpen(true),
    },
  ];

  return (
    <>
      <MobileListRow
        testId="investment-booking-mobile-row"
        title={
          <>
            <BookingType type={booking.bookingType} size="small" />
            {booking.isCancelled && (
              <CancelledIcon
                role="img"
                aria-label={t("investment.protocolTable.cancelled")}
                width={14}
                height={14}
                style={iconStyle}
              />
            )}
            {booking.comment && (
              <ChatIcon
                role="img"
                aria-label={booking.comment}
                width={14}
                height={14}
                style={iconStyle}
              />
            )}
            {quantity && (
              <Typography
                variant="caption"
                color="textSecondary"
                noWrap
                minWidth={0}
              >
                {quantity}
              </Typography>
            )}
          </>
        }
        titleEnd={
          <GainLossTypography
            value={booking.amount}
            variant="body4"
            component="span"
            flex="none"
            whiteSpace="nowrap"
          >
            {toCurrency(booking.amount, booking.currency, false, CENTS)}
          </GainLossTypography>
        }
        subtitle={
          <>
            {booking.priceChange ? (
              <Typography variant="caption" color="textSecondary" noWrap>
                {t("investment.protocolTable.gainersAndLosersLabel")}{" "}
                <GainLossTypography
                  value={booking.priceChange}
                  variant="caption2"
                  component="span"
                  display="inline-flex"
                >
                  {toPercent(booking.priceChange, "exceptZero")}
                </GainLossTypography>
              </Typography>
            ) : null}
            {booking.expenses > 0 && (
              <Typography variant="caption" color="textSecondary" noWrap>
                {t("investment.protocolTable.expenses")}{" "}
                {toCurrency(booking.expenses, booking.currency, false, CENTS)}
              </Typography>
            )}
            {transactionNeedsAttention(status) && (
              <TransactionStatusIcon {...status} size={14} />
            )}
          </>
        }
        subtitleEnd={
          <Typography variant="caption" color="textSecondary" flex="none">
            {date}
          </Typography>
        }
        trailing={
          <ResponsiveDropdown
            id={`booking-actions-${booking.id}`}
            mobileTitle={`${t(`bookingTypes.${booking.bookingType}`)} · ${date}`}
            items={actions}
            anchor={
              <IconButton
                size="small"
                aria-label={t("investment.protocolTable.actions.title")}
                data-testid="booking-actions-button"
                sx={{ mr: -1, color: "icon.secondary" }}
              >
                <DotsIcon width="22px" height="22px" />
              </IconButton>
            }
          />
        }
      />
      <BookingsTableDeleteAction
        dialogOpen={isDeleteOpen}
        bookingId={booking.id}
        onClose={() => setIsDeleteOpen(false)}
      />
    </>
  );
};
